"use client";

import * as React from "react";
import {
  Activity,
  Blocks,
  Folder,
  HandCoins,
  LayoutGrid,
  MinusCircle,
  Settings,
  ShoppingBag,
  Ticket,
  User,
  Users,
  Building2,
  Calendar,
} from "lucide-react";

import { NavMain } from "@/components/ui/nav-main";
import { NavUser } from "@/components/ui/nav-user";
import { TeamSwitcher } from "@/components/ui/team-switcher";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarRail,
} from "@/components/ui/sidebar";
import { useAuth } from "@/context/auth.context";
import { useLocation } from "react-router-dom";
import { useTranslation } from 'react-i18next';

// This is sample data.

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const { user } = useAuth();
  const { t } = useTranslation();
  const pathname = useLocation().pathname;

  const data = React.useMemo(() => {
    return {
      user: {
        name: user?.name,
        email: user?.email,
        profileUrl: user?.profileUrl,
        role: user?.role?.name || t("common.member"),
        isAdmin: user?.isAdmin,
      },
      teams: [{ name: "VJN", id: "4567" }].map((e) => {
        return {
          id: e.id,
          name: e.name,
          plan: t("common.enterprise"),
        };
      }),
      navMain: [
        {
          title: t("nav.dashboard"),
          url: "/dashboard",
          icon: LayoutGrid,
          isActive: pathname.startsWith("/dashboard"),
        },
        ...(user?.isAdmin || user?.role?.name === "President" || user?.role?.name === "Accountant" || user?.role?.name === "Secretary" ? [{
          title: t("nav.savingsMembers"),
          url: "/members",
          icon: User,
          isActive: pathname.startsWith("/members"),
        }] : []),
        ...(user?.isAdmin || user?.role?.name === "President" || user?.role?.name === "Accountant" || user?.role?.name === "Secretary" ? [{
          title: t("nav.contributions"),
          url: "#",
          icon: HandCoins,
          isActive: pathname.startsWith("/contributions") || pathname.startsWith("/solidarity"),
          items: [
            {
              title: t("nav.contributions"),
              url: "/contributions",
            },
            {
              title: t("nav.solidarity"),
              url: "/solidarity",
            },
          ],
        }] : []),
        ...(user?.isAdmin || user?.role?.name === "President" || user?.role?.name === "Accountant" || user?.role?.name === "Secretary" ? [{
          title: t("nav.loansPayments"),
          url: "/loans",
          icon: MinusCircle,
          isActive: pathname.startsWith("/loans"),
        }] : []),
        // fines & penalties
        ...(user?.isAdmin || user?.role?.name === "President" || user?.role?.name === "Accountant" || user?.role?.name === "Secretary" ? [{
          title: t("nav.finesPenalties"),
          url: "/fines",
          icon: Ticket,
          isActive: pathname.startsWith("/fines"),
        }] : []),
        // expenses
        ...(user?.isAdmin || user?.role?.name === "President" || user?.role?.name === "Accountant" || user?.role?.name === "Secretary" ? [{
          title: t("nav.expensesBills"),
          url: "/expenses",
          icon: ShoppingBag,
          isActive: pathname.startsWith("/expenses"),
        }] : []),
        ...(user?.isAdmin || user?.role?.name === "President" || user?.role?.name === "Accountant" || user?.role?.name === "Secretary" ? [{
          title: t("nav.savingGroups"),
          url: "/saving-groups",
          icon: Users,
          isActive: pathname.startsWith("/saving-groups"),
        }] : []),
        ...(user?.isAdmin ? [{
          title: t("nav.districts"),
          url: "/districts",
          icon: Building2,
          isActive: pathname.startsWith("/districts"),
        }] : []),
        ...(user?.isAdmin ? [{
          title: t("nav.sectors"),
          url: "/zones",
          icon: Folder,
          isActive: pathname.startsWith("/zones"),
        }] : []),
        {
          title: t("nav.savingCircles"),
          url: "/saving-seasons",
          icon: Blocks,
          isActive: pathname.startsWith("/saving-seasons"),
        },
        ...(user?.isAdmin || user?.role?.name === "President" || user?.role?.name === "Accountant" || user?.role?.name === "Secretary" ? [{
          title: t("nav.attendance"),
          url: "/attendance",
          icon: Calendar,
          isActive: pathname.startsWith("/attendance"),
        }] : []),
        ...(user?.isAdmin || user?.role?.name === "President" || user?.role?.name === "Accountant" || user?.role?.name === "Secretary" ? [{
          title: t("nav.reportsAnalytics"),
          url: "#",
          icon: Activity,
          isActive: pathname.startsWith("/reports"),
          items: [
            {
              title: t("nav.contributionsReports"),
              url: "/reports/contributions-reports",
            },
            {
              title: t("nav.loansReports"),
              url: "/reports/loans-reports",
            },
            {
              title: t("nav.generalSeasonReports"),
              url: "/reports/general-season-reports",
            },
            {
              title: t("nav.expenseReports"),
              url: "/reports/expense-reports",
            },
          ],
        }] : []),
      ],
      projects: [
        {
          title: t("nav.settings"),
          url: "#",
          icon: Settings,
          isActive: pathname.startsWith("/settings"),
          items: [
            {
              title: t("nav.generalInformation"),
              url: "/settings/general-information",
            },
            {
              title: t("nav.accountSettings"),
              url: "/settings/account-settings",
            },
            // expense categories
            {
              title: t("nav.expenseCategories"),
              url: "/settings/expense-categories",
            },
            // payment methods
            {
              title: t("nav.paymentMethods"),
              url: "/settings/payment-methods",
            }, // role & permissions
            {
              title: t("nav.rolePermissions"),
              url: "/settings/role-permissions",
            },
            // system users
            {
              title: t("nav.systemUsers"),
              url: "/settings/system-users",
            },
          ],
        },
      ],
    };
  }, [user, pathname, t]);
  return (
    <Sidebar collapsible="icon" {...props}>
      <SidebarHeader>
        <TeamSwitcher teams={data.teams} />
      </SidebarHeader>
      <SidebarContent>
        <NavMain title={t("common.platformManagement")} items={data.navMain} />
        <NavMain title={t("common.configurationSettings")} items={data.projects} />
      </SidebarContent>
      <SidebarFooter>
        <NavUser user={data.user} />
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}
